import {CheckCircle, XCircle, ChevronDown, ChevronUp} from 'lucide-react';
import {useState} from 'react';
import type {CodeRunResponse, CodeRunTestCase} from 'iq-questions/types';

interface RunResultsProps {
  result: CodeRunResponse;
}

export function RunResults({result}: RunResultsProps) {
  const isPlayground = result.test_cases?.some((tc) => tc.playground);

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {!isPlayground && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium ${
            result.success
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
          {result.success ? (
            <CheckCircle className='w-4 h-4 shrink-0' />
          ) : (
            <XCircle className='w-4 h-4 shrink-0' />
          )}
          {result.message}
        </div>
      )}

      {result.test_cases?.map((tc, i) => <TestCase key={tc.id} index={i} tc={tc} />)}
    </div>
  );
}

function TestCase({tc, index}: {tc: CodeRunTestCase; index: number}) {
  const [open, setOpen] = useState(true);

  return (
    <div
      className={`border rounded overflow-hidden text-sm ${tc.success ? 'border-green-200' : 'border-red-200'}`}>
      <button
        className={`w-full flex items-center justify-between px-3 py-2 font-medium text-left ${
          tc.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}
        onClick={() => setOpen((o) => !o)}>
        <span className='flex items-center gap-2'>
          {tc.success ? (
            <CheckCircle className='w-4 h-4 shrink-0' />
          ) : (
            <XCircle className='w-4 h-4 shrink-0' />
          )}
          {tc.playground ? 'Playground' : `Test case #${index + 1}`}
        </span>
        {open ? <ChevronUp className='w-3.5 h-3.5' /> : <ChevronDown className='w-3.5 h-3.5' />}
      </button>

      {open && (
        <div className='flex flex-col gap-2 p-3 bg-white'>
          {tc.stderr && <OutputBlock label='stderr' variant='error' content={tc.stderr} />}
          {tc.stdout && <OutputBlock label='stdout' variant='info' content={tc.stdout} />}
          {tc.output && (
            <OutputBlock
              label='Your output'
              variant={tc.success ? 'success' : 'fail'}
              content={tc.output}
            />
          )}
          {tc.expected_output && (
            <OutputBlock label='Expected output' variant='info' content={tc.expected_output} />
          )}
          <p className='text-xs text-iq-grey-medium'>
            Execution time:{' '}
            {tc.runtime < 1000 ? `${tc.runtime} ms` : `${(tc.runtime / 1000).toFixed(2)} s`}
          </p>
        </div>
      )}
    </div>
  );
}

function OutputBlock({
  label,
  variant,
  content,
}: {
  label: string;
  variant: 'error' | 'info' | 'success' | 'fail';
  content: string;
}) {
  const headerCls = {
    error: 'bg-red-100 text-red-700',
    info: 'bg-gray-100 text-iq-grey-dark',
    success: 'bg-green-100 text-green-700',
    fail: 'bg-orange-100 text-orange-700',
  }[variant];

  const parsed = tryParseTable(content);

  return (
    <div className='border border-iq-grey-light rounded overflow-hidden'>
      <div className={`px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${headerCls}`}>
        {label}
      </div>
      <div className='p-2 bg-white'>
        {parsed ? (
          <DataTable rows={parsed} />
        ) : (
          <pre className='text-xs text-iq-grey-dark whitespace-pre-wrap break-words font-mono'>
            {content}
          </pre>
        )}
      </div>
    </div>
  );
}

function DataTable({rows}: {rows: Record<string, unknown>[]}) {
  if (rows.length === 0) return <p className='text-xs text-iq-grey-medium italic'>empty</p>;
  const cols = Object.keys(rows[0] ?? {});
  return (
    <div className='overflow-x-auto'>
      <table className='text-xs w-full border-collapse'>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                className='border border-iq-grey-light px-2 py-1 bg-gray-50 text-left font-semibold text-iq-grey-dark'>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c} className='border border-iq-grey-light px-2 py-1 text-iq-grey-dark'>
                  {String(row[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function tryParseTable(content: string): Record<string, unknown>[] | null {
  try {
    const parsed = JSON.parse(content);
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      typeof parsed[0] === 'object' &&
      parsed[0] !== null
    ) {
      return parsed as Record<string, unknown>[];
    }
  } catch {
    // not JSON
  }
  return null;
}
